<template>
  <div class="fp-canvas-wrap">
    <!-- Legend -->
    <div v-if="showLegend && sectionLegend.length" class="fp-legend">
      <span
        v-for="s in sectionLegend"
        :key="s.id"
        class="fp-legend-item"
        :style="{ background: SECTION_COLOURS[s.colour]?.table }"
      >{{ s.name }}</span>
      <span v-if="hasPoolTable" class="fp-legend-item fp-legend-item--pool">Pool Table</span>
    </div>

    <!-- Mode toggle (interactive only) -->
    <div v-if="interactive && showModeToggle" class="fp-mode-toggle">
      <button
        v-for="m in availableModes"
        :key="m.value"
        class="fp-mode-btn"
        :class="{ 'fp-mode-btn--active': currentMode === m.value }"
        @click="setMode(m.value)"
        :title="m.description"
      >{{ m.label }}</button>
    </div>
    <p v-if="interactive && showModeToggle" class="fp-mode-hint">{{ modeHints[currentMode] }}</p>

    <!-- SVG canvas -->
    <svg
      class="fp-svg"
      :viewBox="`0 0 ${layout.canvasW} ${layout.canvasH}`"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- ── Section backgrounds ── -->
      <g v-for="sec in layout.sections" :key="sec.id">
        <rect
          :x="sec.x" :y="sec.y" :width="sec.w" :height="sec.h"
          :fill="sectionBgFill(sec)"
          :stroke="sectionStroke(sec)"
          stroke-width="2.5"
          :stroke-dasharray="isSectionSelected(sec.id) ? '' : ''"
          rx="8"
          :class="{ 'fp-clickable': interactive && (currentMode === 'section' || currentMode === 'venue') }"
          @click="handleSectionClick(sec.id)"
        />
        <text
          :x="sec.x + sec.w / 2" :y="sec.y + 20"
          text-anchor="middle"
          :fill="sectionTextColour(sec)"
          font-family="var(--font-heading)"
          font-size="13" font-weight="400" letter-spacing="1"
          pointer-events="none"
        >{{ sec.name }}</text>
      </g>

      <!-- ── Fixtures (non-bookable elements) ── -->
      <g v-for="fix in layout.fixtures" :key="fix.id">
        <!-- Pool table — interactive in pool_table mode -->
        <template v-if="fix.fixtureType === 'pool_table'">
          <rect
            :x="fix.x" :y="fix.y" :width="fix.w" :height="fix.h"
            :fill="poolTableFill(fix)"
            :stroke="poolTableStroke(fix)"
            stroke-width="3" rx="8"
            :class="{ 'fp-clickable': interactive && currentMode === 'pool_table' }"
            @click="handlePoolTableClick(fix)"
          />
          <text
            :x="fix.x + fix.w / 2" :y="fix.y + fix.h / 2 - 8"
            text-anchor="middle" dominant-baseline="middle"
            fill="white"
            font-family="var(--font-heading)"
            font-size="13" pointer-events="none"
          >{{ fix.label }}</text>
          <text
            v-if="interactive && currentMode === 'pool_table' && !isPoolTableBooked(fix)"
            :x="fix.x + fix.w / 2" :y="fix.y + fix.h / 2 + 10"
            text-anchor="middle" fill="rgba(255,255,255,0.7)"
            font-family="var(--font-body)"
            font-size="10" pointer-events="none"
          >{{ poolPriceLabel }} — tap to book</text>
          <text
            v-if="isPoolTableBooked(fix)"
            :x="fix.x + fix.w / 2" :y="fix.y + fix.h / 2 + 10"
            text-anchor="middle" fill="#e74c3c"
            font-family="var(--font-body)"
            font-size="10" font-weight="600" pointer-events="none"
          >BOOKED</text>
        </template>

        <!-- Tree — circle -->
        <template v-else-if="fix.fixtureType === 'tree'">
          <circle
            :cx="fix.x + fix.w / 2" :cy="fix.y + fix.h / 2"
            :r="Math.min(fix.w, fix.h) / 2"
            :fill="fix.colour || '#2d6a04'" opacity="0.85"
          />
          <text
            :x="fix.x + fix.w / 2" :y="fix.y + fix.h / 2 + 4"
            text-anchor="middle" dominant-baseline="middle"
            fill="white" font-family="var(--font-body)"
            font-size="11" pointer-events="none"
          >{{ fix.label }}</text>
        </template>

        <!-- Divider — line -->
        <template v-else-if="fix.fixtureType === 'divider'">
          <line
            :x1="fix.x" :y1="fix.y"
            :x2="fix.x + fix.w" :y2="fix.y + fix.h"
            :stroke="fix.colour || 'rgba(0,0,0,0.15)'"
            stroke-width="1.5" stroke-dasharray="4,3"
          />
          <text
            v-if="fix.label"
            :x="fix.x + fix.w / 2" :y="fix.y + fix.h / 2"
            text-anchor="middle"
            :fill="fix.colour || 'rgba(0,0,0,0.35)'"
            font-family="var(--font-body)"
            font-size="9" pointer-events="none"
          >{{ fix.label }}</text>
        </template>

        <!-- Label only -->
        <template v-else-if="fix.fixtureType === 'label'">
          <text
            :x="fix.x + fix.w / 2" :y="fix.y + fix.h / 2 + 4"
            text-anchor="middle" dominant-baseline="middle"
            :fill="fix.colour || 'var(--jb-color-brand)'"
            font-family="var(--font-heading)"
            font-size="16" font-weight="400" letter-spacing="2"
            pointer-events="none"
          >{{ fix.label }}</text>
        </template>

        <!-- All other fixtures (kitchen, bar_counter, door, cafe, generic) — rect + label -->
        <template v-else>
          <g :transform="fix.rotation ? `rotate(${fix.rotation}, ${fix.x + fix.w / 2}, ${fix.y + fix.h / 2})` : undefined">
            <rect
              :x="fix.x" :y="fix.y" :width="fix.w" :height="fix.h"
              :fill="fixtureFill(fix)"
              rx="4"
            />
            <text
              :x="fix.x + fix.w / 2" :y="fix.y + fix.h / 2 + 4"
              text-anchor="middle" dominant-baseline="middle"
              :fill="fixtureTextColour(fix)"
              font-family="var(--font-body)"
              font-size="12" font-weight="400" letter-spacing="0.5"
              pointer-events="none"
            >{{ fix.label }}</text>
          </g>
        </template>
      </g>

      <!-- ── Tables ── -->
      <g v-for="tbl in layout.tables" :key="tbl.id">
        <rect
          v-if="tbl.shape !== 'round'"
          :x="tbl.x" :y="tbl.y" :width="tbl.w" :height="tbl.h"
          :fill="tableFill(tbl)"
          :stroke="tableStroke(tbl)"
          stroke-width="2" rx="6"
          :class="{ 'fp-clickable': (interactive && currentMode === 'table') || bookingDropEnabled, 'fp-drop-target': dragOverTableId === tableNumId(tbl) }"
          @click="handleTableClick(tbl)"
          @dragover.prevent="handleTableDragOver(tbl)"
          @dragleave="handleTableDragLeave(tbl)"
          @drop.prevent="handleTableDrop($event, tbl)"
        />
        <ellipse
          v-else
          :cx="tbl.x + tbl.w / 2" :cy="tbl.y + tbl.h / 2"
          :rx="tbl.w / 2" :ry="tbl.h / 2"
          :fill="tableFill(tbl)"
          :stroke="tableStroke(tbl)"
          stroke-width="2"
          :class="{ 'fp-clickable': (interactive && currentMode === 'table') || bookingDropEnabled, 'fp-drop-target': dragOverTableId === tableNumId(tbl) }"
          @click="handleTableClick(tbl)"
          @dragover.prevent="handleTableDragOver(tbl)"
          @dragleave="handleTableDragLeave(tbl)"
          @drop.prevent="handleTableDrop($event, tbl)"
        />
        <text
          :x="tbl.x + tbl.w / 2" :y="tbl.y + tbl.h / 2"
          text-anchor="middle" dominant-baseline="middle"
          fill="white" font-family="var(--font-body)"
          font-size="14" font-weight="500" pointer-events="none"
        >{{ tbl.label }}</text>
        <text
          v-if="tableCapacities[tableNumId(tbl)] || tbl.capacity"
          :x="tbl.x + tbl.w / 2" :y="tbl.y + tbl.h / 2 + 14"
          text-anchor="middle"
          fill="rgba(255,255,255,0.7)"
          font-family="var(--font-body)"
          font-size="9" pointer-events="none"
        >{{ tableCapacities[tableNumId(tbl)] ?? tbl.capacity }}p</text>
      </g>

      <!-- ── Entire venue selection overlay ── -->
      <rect
        v-if="internalSelection.entireVenue"
        x="4" y="4"
        :width="layout.canvasW - 8" :height="layout.canvasH - 8"
        fill="rgba(39,174,96,0.07)"
        stroke="#27ae60" stroke-width="3" stroke-dasharray="10,5"
        rx="10" pointer-events="none"
      />
    </svg>

    <!-- Selection summary pill -->
    <div v-if="interactive && hasSelection" class="fp-selection-pill">
      <span v-if="internalSelection.entireVenue">Entire venue selected</span>
      <span v-else-if="internalSelection.selectedSectionId">
        {{ getSectionName(internalSelection.selectedSectionId) }} selected
      </span>
      <span v-else-if="internalSelection.selectedTableIds.length">
        {{ internalSelection.selectedTableIds.length }} table(s): {{ internalSelection.selectedTableIds.join(', ') }}
      </span>
      <span v-else-if="internalSelection.selectedPoolTableId">
        Pool Table — {{ poolPriceLabel }} × {{ internalSelection.poolTableHours || 1 }}hr
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { formatCurrency } from '../utils/formatters'
import { TABLE_STATUS_COLOURS } from '../floorPlanColours'
import type {
  FloorPlanLayout,
  LayoutTable,
  LayoutSection,
  LayoutFixture,
  FloorPlanSelection,
  SelectionMode,
  TableBookingStatus,
  SectionBookingStatus,
} from '../types'

const props = withDefaults(defineProps<{
  layout: FloorPlanLayout
  modelValue?: FloorPlanSelection
  tableStatuses?: TableBookingStatus[]
  sectionStatuses?: SectionBookingStatus[]
  tableCapacities?: Record<number, number>
  interactive?: boolean
  /** Allow external staff booking cards to be dropped onto table targets. */
  bookingDropEnabled?: boolean
  showLegend?: boolean
  showModeToggle?: boolean
  /** Colour free tables as Available instead of by section (service views, where colour means status). */
  statusColours?: boolean
  poolTablePricePerHour?: number
  locale?: string
  currency?: string
}>(), {
  interactive: true,
  showLegend: true,
  showModeToggle: true,
  statusColours: false,
  tableStatuses: () => [],
  sectionStatuses: () => [],
  tableCapacities: () => ({}),
  bookingDropEnabled: false,
  poolTablePricePerHour: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [sel: FloorPlanSelection]
  'table-click': [tableId: number]
  'table-drop': [tableId: number, bookingId: string]
  'section-click': [sectionId: string]
  'pool-table-click': [poolTableId: string]
}>()

// ── Mode ───────────────────────────────────────────────────────────────────────
const currentMode = ref<SelectionMode>('table')
const dragOverTableId = ref<number | null>(null)

const hasPoolTable = computed(() =>
  props.layout.fixtures.some((f) => f.fixtureType === 'pool_table')
)

const poolPriceLabel = computed(() =>
  props.poolTablePricePerHour !== undefined ? `${formatCurrency(props.poolTablePricePerHour, props.currency, props.locale)}/hr` : 'pool table'
)

const availableModes = computed(() => {
  const modes = [
    { value: 'table' as SelectionMode, label: 'Reserve a Table', description: 'Select tables — no deposit' },
    { value: 'section' as SelectionMode, label: 'Book a Section', description: 'Exclusive section booking' },
    { value: 'venue' as SelectionMode, label: 'Book Entire Venue', description: 'Full exclusive use' },
  ]
  if (hasPoolTable.value) {
    modes.push({ value: 'pool_table' as SelectionMode, label: 'Book Pool Table', description: `${poolPriceLabel.value} exclusive access` })
  }
  return modes
})

const modeHints = computed((): Record<SelectionMode, string> => ({
  table: 'Tap tables to select. No deposit required.',
  section: 'Tap a section to book it exclusively. Minimum spend + deposit.',
  venue: 'The entire venue is exclusively yours. Minimum spend + deposit.',
  pool_table: `Tap the pool table for exclusive access at ${poolPriceLabel.value}. Full payment required.`,
}))

function setMode(mode: SelectionMode) {
  currentMode.value = mode
  const newSel: FloorPlanSelection = {
    mode,
    selectedTableIds: [],
    selectedSectionId: null,
    entireVenue: mode === 'venue',
    selectedPoolTableId: null,
    poolTableHours: 1,
  }
  internalSelection.value = newSel
  emit('update:modelValue', newSel)
}

// ── Selection ──────────────────────────────────────────────────────────────────
const internalSelection = ref<FloorPlanSelection>(
  props.modelValue || {
    mode: 'table', selectedTableIds: [], selectedSectionId: null,
    entireVenue: false, selectedPoolTableId: null, poolTableHours: 1,
  }
)

watch(() => props.modelValue, (v) => {
  if (v) {
    internalSelection.value = v
    currentMode.value = v.mode
  }
})

const hasSelection = computed(() => {
  const s = internalSelection.value
  return s.entireVenue || !!s.selectedSectionId || s.selectedTableIds.length > 0 || !!s.selectedPoolTableId
})

// ── Colours ────────────────────────────────────────────────────────────────────
const SECTION_COLOURS: Record<string, { bg: string; stroke: string; text: string; table: string }> = {
  orange: { bg: 'rgba(232,144,42,0.12)', stroke: '#e8902a', text: '#c07318', table: '#e8902a' },
  red:    { bg: 'rgba(192,57,43,0.10)',  stroke: '#c0392b', text: '#a02820', table: '#c0392b' },
  blue:   { bg: 'rgba(58,128,200,0.10)', stroke: '#3a80c8', text: '#2a60a0', table: '#3a80c8' },
  green:  { bg: 'rgba(45,106,4,0.10)',   stroke: '#3d8b25', text: '#2a6a10', table: '#3d8b25' },
}

const FIXTURE_FILLS: Partial<Record<string, string>> = {
  kitchen:     '#1a1a1a',
  door:        '#1a1a1a',
  bar_counter: 'rgba(184,116,10,0.65)',
  pool_table:  'rgba(45,106,4,0.35)',
  cafe:        'rgba(58,128,200,0.7)',
  generic:     'rgba(120,120,120,0.5)',
}

const FIXTURE_TEXT: Partial<Record<string, string>> = {
  kitchen:     'white',
  door:        'white',
  bar_counter: 'white',
  pool_table:  'white',
  cafe:        'white',
  generic:     'white',
}

function fixtureFill(fix: LayoutFixture): string {
  return fix.colour || FIXTURE_FILLS[fix.fixtureType] || '#aaa'
}

function fixtureTextColour(fix: LayoutFixture): string {
  return FIXTURE_TEXT[fix.fixtureType] || 'white'
}

// ── Section helpers ────────────────────────────────────────────────────────────
function isSectionSelected(id: string): boolean {
  if (internalSelection.value.entireVenue) return true
  return internalSelection.value.selectedSectionId === id
}

function getSectionStatus(id: string) {
  return props.sectionStatuses.find((s) => s.sectionId === id)
}

function sectionBgFill(sec: LayoutSection): string {
  const status = getSectionStatus(sec.id)
  if (status?.status === 'booked') return 'rgba(231,76,60,0.12)'
  if (isSectionSelected(sec.id)) return 'rgba(39,174,96,0.14)'
  return SECTION_COLOURS[sec.colour]?.bg || 'rgba(200,200,200,0.08)'
}

function sectionStroke(sec: LayoutSection): string {
  const status = getSectionStatus(sec.id)
  if (status?.status === 'booked') return '#e74c3c'
  if (isSectionSelected(sec.id)) return '#27ae60'
  return SECTION_COLOURS[sec.colour]?.stroke || '#888'
}

function sectionTextColour(sec: LayoutSection): string {
  if (isSectionSelected(sec.id)) return '#1e8449'
  return SECTION_COLOURS[sec.colour]?.text || '#555'
}

function getSectionName(id: string): string {
  return props.layout.sections.find((s) => s.id === id)?.name || id
}

// ── Table helpers ──────────────────────────────────────────────────────────────
function tableNumId(tbl: LayoutTable): number {
  return parseInt(tbl.label, 10) || 0
}

function getTableSection(tbl: LayoutTable): LayoutSection | undefined {
  return props.layout.sections.find((s) => s.id === tbl.sectionId)
}

function getTableStatus(tbl: LayoutTable) {
  const numId = tableNumId(tbl)
  return props.tableStatuses.find((t) => t.tableId === numId)
}

function isTableSelected(tbl: LayoutTable): boolean {
  if (internalSelection.value.entireVenue) return true
  const sec = getTableSection(tbl)
  if (sec && internalSelection.value.selectedSectionId === sec.id) return true
  const numId = tableNumId(tbl)
  return internalSelection.value.selectedTableIds.includes(numId)
}

function tableFill(tbl: LayoutTable): string {
  const status = getTableStatus(tbl)
  if ((status?.status as string) === 'seated') return TABLE_STATUS_COLOURS.seated  // bright green — occupied now
  if (status?.status === 'booked') return TABLE_STATUS_COLOURS.booked
  if (status?.status === 'deposit_pending') return TABLE_STATUS_COLOURS.deposit_pending
  if (isTableSelected(tbl)) return '#27ae60'
  if (props.statusColours) return TABLE_STATUS_COLOURS.available
  const sec = getTableSection(tbl)
  return SECTION_COLOURS[sec?.colour || 'blue']?.table || '#3a80c8'
}

function tableStroke(tbl: LayoutTable): string {
  const status = getTableStatus(tbl)
  if (isTableSelected(tbl)) return '#1e8449'
  if ((status?.status as string) === 'seated') return '#27ae60'
  if (status?.status === 'booked') return '#c0392b'
  if (status?.status === 'deposit_pending') return '#b8740a'
  return 'rgba(255,255,255,0.35)'
}

// ── Pool table helpers ─────────────────────────────────────────────────────────
function isPoolTableBooked(fix: LayoutFixture): boolean {
  return props.sectionStatuses.some((s) => s.sectionId === 'pool-table' || s.sectionId === fix.id)
}

function isPoolTableSelected(fix: LayoutFixture): boolean {
  return internalSelection.value.selectedPoolTableId === fix.poolTableId
}

function poolTableFill(fix: LayoutFixture): string {
  if (isPoolTableBooked(fix)) return 'rgba(231,76,60,0.25)'
  if (isPoolTableSelected(fix)) return 'rgba(39,174,96,0.25)'
  return fix.colour || 'rgba(45,106,4,0.35)'
}

function poolTableStroke(fix: LayoutFixture): string {
  if (isPoolTableBooked(fix)) return '#e74c3c'
  if (isPoolTableSelected(fix)) return '#27ae60'
  return '#2d6a04'
}

// ── Legend ─────────────────────────────────────────────────────────────────────
const sectionLegend = computed(() => {
  const seen = new Set<string>()
  return props.layout.sections.filter((s) => {
    if (seen.has(s.colour)) return false
    seen.add(s.colour)
    return true
  })
})

// ── Event handlers ─────────────────────────────────────────────────────────────
function handleTableClick(tbl: LayoutTable) {
  if (props.bookingDropEnabled) emit('table-click', tableNumId(tbl))
  if (!props.interactive || currentMode.value !== 'table') return
  const numId = tableNumId(tbl)
  const current = [...internalSelection.value.selectedTableIds]
  const idx = current.indexOf(numId)
  if (idx >= 0) current.splice(idx, 1)
  else current.push(numId)
  const newSel: FloorPlanSelection = {
    mode: 'table', selectedTableIds: current,
    selectedSectionId: null, entireVenue: false,
  }
  internalSelection.value = newSel
  emit('update:modelValue', newSel)
  emit('table-click', numId)
}

function handleTableDragOver(tbl: LayoutTable) {
  if (props.bookingDropEnabled) dragOverTableId.value = tableNumId(tbl)
}

function handleTableDragLeave(tbl: LayoutTable) {
  if (dragOverTableId.value === tableNumId(tbl)) dragOverTableId.value = null
}

function handleTableDrop(event: DragEvent, tbl: LayoutTable) {
  dragOverTableId.value = null
  if (!props.bookingDropEnabled) return
  const bookingId = event.dataTransfer?.getData('application/x-jb-booking')
    || event.dataTransfer?.getData('text/plain')
  if (bookingId) emit('table-drop', tableNumId(tbl), bookingId)
}

function handleSectionClick(sectionId: string) {
  if (!props.interactive) return
  if (currentMode.value === 'venue') {
    const newSel: FloorPlanSelection = {
      mode: 'venue', selectedTableIds: [],
      selectedSectionId: null, entireVenue: true,
    }
    internalSelection.value = newSel
    emit('update:modelValue', newSel)
    return
  }
  if (currentMode.value === 'section') {
    const newId = internalSelection.value.selectedSectionId === sectionId ? null : sectionId
    const newSel: FloorPlanSelection = {
      mode: 'section', selectedTableIds: [],
      selectedSectionId: newId, entireVenue: false,
    }
    internalSelection.value = newSel
    emit('update:modelValue', newSel)
    emit('section-click', sectionId)
  }
}

function handlePoolTableClick(fix: LayoutFixture) {
  if (!props.interactive || currentMode.value !== 'pool_table') return
  const poolTableId = fix.poolTableId || fix.id
  const newId = internalSelection.value.selectedPoolTableId === poolTableId ? null : poolTableId
  const newSel: FloorPlanSelection = {
    mode: 'pool_table', selectedTableIds: [],
    selectedSectionId: null, entireVenue: false,
    selectedPoolTableId: newId,
    poolTableHours: internalSelection.value.poolTableHours || 1,
  }
  internalSelection.value = newSel
  emit('update:modelValue', newSel)
  emit('pool-table-click', poolTableId)
}
</script>

<style scoped>
.fp-canvas-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 10px;
  padding: 16px;
  border: 1px solid color-mix(in srgb, var(--jb-color-brand) 10%, transparent);
}

.fp-svg {
  width: 100%;
  height: auto;
  display: block;
}

.fp-clickable {
  cursor: pointer;
  transition: opacity 0.15s ease;
}
.fp-clickable:hover { opacity: 0.82; }

.fp-drop-target { opacity: 0.72; filter: drop-shadow(0 0 7px rgba(39, 174, 96, 0.9)); }

/* Legend */
.fp-legend {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.fp-legend-item {
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 32px;
  color: white;
}

.fp-legend-item--pool {
  background: #3d8b25;
}

/* Mode toggle */
.fp-mode-toggle {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  background: color-mix(in srgb, var(--jb-color-brand) 6%, transparent);
  padding: 5px;
  border-radius: 32px;
  width: fit-content;
  border: 1px solid color-mix(in srgb, var(--jb-color-brand) 12%, transparent);
}

.fp-mode-btn {
  padding: 7px 16px;
  border: none;
  background: transparent;
  border-radius: 32px;
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: color-mix(in srgb, var(--jb-color-brand) 60%, transparent);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.fp-mode-btn:hover { background: color-mix(in srgb, var(--jb-color-brand) 8%, transparent); color: var(--jb-color-brand); }
.fp-mode-btn--active { background: var(--jb-color-brand); color: var(--jb-color-background); }

.fp-mode-hint {
  font-family: var(--font-body);
  font-size: 0.82rem;
  color: color-mix(in srgb, var(--jb-color-brand) 60%, transparent);
  margin: 0;
  line-height: 1.4;
}

/* Selection pill */
.fp-selection-pill {
  padding: 9px 16px;
  background: color-mix(in srgb, var(--jb-color-brand) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--jb-color-brand) 20%, transparent);
  border-radius: 32px;
  color: var(--jb-color-brand);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 500;
}
</style>
